import { Component, OnInit, AfterViewInit, OnChanges, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { Place } from '@app/modules/model/place';
import { Ticket } from '@app/modules/model/ticket';
import { TicketMixedPayment } from '@app/modules/model/ticketmixed';
import { Discount } from '@app/modules/model/discount';
import { TicketProduct } from '@app/modules/model/ticketproduct';
import { TicketOption } from '@app/modules/model/ticketoption';
import { TicketDiscount } from '@app/modules/model/ticketdiscount';

import { AppConstants } from '@app/app.constants';
import { GenericUtils } from '@app/app.commonutils';
import { languageSupport, languageService } from '@app/modules/common/language'
import { toastService } from '@app/modules/common/toast';
import { alertService } from '@app/modules/common/alert';
import { pickPaymentTerminal, placeBlocksCash, resolvePaymentTerminals, isPaymentTerminalManual } from '@app/components/model/ticket/ui-paytef/ui-paytef-utils';
import { syncService } from '@app/modules/sync';
import { dataService } from '@app/modules/data';
import { viewService } from '@app/modules/view';

import { UiCashPayModal } from '@app/components/model/ticket/ui-cash/ui-cash';
import { UiCashGuardCashPayModal } from '@app/components/model/ticket/ui-cashguard-cash/ui-cashguard-cash';
import { cashlessRefundStore } from '@app/components/model/ticket/ui-cashguard-cash/cashless-refund-store';
import { tryCashlessCardPayment, chooseCardTerminal, CashlessModalDeps } from '@app/components/model/ticket/ui-cashguard-cash/ui-cashless-utils';
import { UiCardPayModal } from '@app/components/model/ticket/ui-card/ui-card';
import { UiPaytefPayModal } from '@app/components/model/ticket/ui-paytef/ui-paytef';
import { UiToPrintertModal } from '@app/components/login/modal/print/ui-toprinter-modal';
import { deviceService } from '@app/modules/sockets';

@Component({
    selector: 'ui-ticket-modify',
    templateUrl:'./ui-edit.html',
    styleUrls: [ 
        './ui-edit.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiTicketEdit extends languageSupport implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input('place') _place: Place = null;
    @Input('ticket') _ticket: Ticket = null;

    private _subscriptions = [];

    private get _user(){
        // Usuario EFECTIVO (fuente única data.EffectiveUser): ver ui-view.
        return this.data.EffectiveUser;
    }

    normalize(str){
        return GenericUtils.Normalize(str);
    }

    /***********************************/
    /* DATA COMPONENTS                 */
    /***********************************/

    get CanLast(){
        return (this._place?.ShowLastClosed || false) && (this.LastTickets.length > 0);
    }

    get NewTicket(){
        return this._ticket.IsEmpty || (this._ticket.status == 'PR');
    }

    get CashpayMaxPrice(){
        return AppConstants.CashpayMaxPrice;
    }

    get InvoiceMaxPrice(){
        return AppConstants.InvoiceMaxPrice;
    }

    get IsTicketUpdate(){
        return (this._ticket.CopyOf != null)
    }

    get CanPrint(){
        return (!this._ticket.IsEmpty);
    }

    get CanSend() {
        // cannot send until synchronized
        if (!this._place.AllowTicketCommit){
            return false;
        }

        // this is a ticket update
        if (this.IsTicketUpdate){
            // Requiere permiso de modificación del usuario EFECTIVO (lock-aware): si
            // no, un editor ya abierto seguía enviando tras bloqueo/cambio de camarero
            // a uno sin AllowTicketUpdate.
            if (!this._place.AllowTicketUpdate(this._user)){
                return false;
            }
            return !this.IsPristine;
        }

        // this is a new ticket
        else {
            return !this._ticket.IsEmpty && !this._ticket.IsPrepay;
        }
    }

    get CashLimit(){
        return (this._ticket.price >= this.CashpayMaxPrice);
    }

    /**
     * Slim-device cash lockout — see placeBlocksCash(). Gate the cash
     * entry points (main PAYCASH button, mixed option) on this getter
     * to honor the per-terminal `nocash` flag. Card / account flows
     * are unaffected.
     */
    get BlockCash(){
        return placeBlocksCash(this._place, this.view);
    }

    get CanCash() {
        // Igual que ui-view: ticket ya pagado (prepago QR en PD, etc.) no se
        // vuelve a cobrar desde el editor. Tras REABRIR se limpian eventos PD
        // y status deja PD → IsPaid=false y los botones se reactivan.
        // Tambien miramos CopyOf: la copia de edicion de un pagado pendiente
        // de confirmar hereda el cobro del original.
        if (this._ticket.IsPaid){
            return false;
        }
        let _src = this._ticket.CopyOf as Ticket;
        if (_src && _src.IsPaid){
            return false;
        }
        return !this._ticket.IsEmpty && this._place.AllowTicketPayment;
    }

    get CanCard() {
        return this.CanCash && this._place.AcceptCard;
    }

    get AllowComanderoCash(){
        return this._place.AllowComanderoCash;
    }

    get AllowComanderoCard(){
        return this._place.AllowComanderoCard;
    }

    get CanMixed(){
        return this.CanCash && this.CanCard && !this.BlockCash;
    }

    get CanAccount(){
        return this._place.AcceptAccount && this.CanCash;
    }

    get CanMerge() {
        return !this._ticket.IsEmpty && (this.MergeTickets.length > 0);
    }    

    get CanSplit(){
        return (this._ticket.status != 'PR') && (this._ticket.Length > 1);
    }

    // In-flight flag: OnTicketSplit es async y tarda ~500ms-1s en await
    // Flush(true). Sin guard, un doble-click rapido (cajero impaciente)
    // mete dos splits: el primero mueve productos a #split1, el segundo
    // los re-mueve a #split2 dejando #split1 vacio (huerfano). CanDoSplit
    // pasa a false durante el split para deshabilitar el boton.
    private _splitting = false;

    get CanDoSplit(){
        return !this._splitting && this.HasSelectedProducts && !this.AllSelectedProducts;
    }

    // In-flight flag para MANDAR/PAGAR (mismo motivo que _splitting en dividir):
    // con el server lento OnCommit tarda en cerrar el editor y un cajero
    // impaciente puede tocar pagar/mandar varias veces -> N DoCommit del MISMO
    // ticket -> N rectificaciones / doble cobro. Mientras hay un commit en
    // curso, IsCommitting deshabilita los botones y OnCommit ignora los re-taps.
    private _committing = false;
    get IsCommitting(){
        return this._committing;
    }

    get CanCancel(){
        return !this.NewTicket && !this._ticket.ToRefund && !this._ticket.invceac;
    }

    private _tabletickets = null;
    get TableTickets(){
        if (!this._ticket.qrcode){
            return [];  // bar ticket
        }

        if (this._tabletickets == null){
            let _tickets = [];
            for(let _ticket of this._ticket.qrcode.tickets){
                // Pagado (p.ej. QR prepago PD pendiente de preparar): no cuenta
                // como comanda editable en mesa. Si no, al click en mesa verde
                // ngAfterViewInit hace Copy() y abre MODIFICANDO en vez de nueva.
                if ((_ticket.IsValid) && (_ticket.IsRecent) && !_ticket.IsPaid && !(_ticket.IsToPay && _ticket.account)){
                    _tickets.push(_ticket);
                }
            }

            // sort the tickets for display
            _tickets = _tickets.sort((a, b) => {
                return b.created.getTime() - a.created.getTime(); 
            })

            this._tabletickets = _tickets;
        }

        return this._tabletickets;
    }

    get LastTickets(){
        return this._place?.GetLastTickets() || [];
    }

    private _discounts: Array<Discount> = null;
    get Discounts(){
        if (this._discounts == null){
            let _discounts = [];

            let _applied = [];
            for (let _ticketdiscount of this._ticket.discounts){
                if (_ticketdiscount.IsValid){
                    _applied.push(_ticketdiscount.discount);
                }
            }

            for (let _discount of this._place.discounts){
                if (_discount.CanApply(_applied)){
                    _discounts.push(_discount);
                }
            }

            this._discounts = _discounts;
        }

        return this._discounts;
    }

    get CanDiscount(){
        return !this._ticket.IsEmpty && (this.Discounts.length > 0);
    }

    private _lastminute = new Date().getMinutes();
    private _checkDiscountStatus(){
        let _dateminute = new Date().getMinutes();

        // refresh on the first second of the next minute
        if (this._lastminute != _dateminute){
            this._discounts = null;
        }

        this._lastminute = _dateminute;
    }

    private _showdiscounts = false;
    get ExpandDiscounts(){
        return this._showdiscounts;
    }

    set ExpandDiscounts(value){
        this._showdiscounts = value;
        this.change.markForCheck();
    }

    OnExpandDiscounts(){
        this.ExpandDiscounts = !this.ExpandDiscounts;
    }

    OnDiscount(_discount){
        let _ticketdiscount = new TicketDiscount(null, this.data);
        if (_ticketdiscount){
            _ticketdiscount.ticket = this._ticket;
            _ticketdiscount.discount = _discount;
            _ticketdiscount.status = 'AC';
        }

        this.ExpandDiscounts = false;
        this._ticket.DiscountAdd(_ticketdiscount);
        this.change.markForCheck();
    }

    public _diners = [ 1, 2, 3, 4, 5 ];

    private _showdiners = false;
    get ShowDiners(){
        return this._showdiners;
    }

    set ShowDiners(value){
        this._showdiners = value;
        this.change.markForCheck();
    }

    OnShowDiners(){
        this.ShowDiners = !this.ShowDiners;
    }

    SetDinersSize(size){
        this._ticket.diners = size;
        this.ShowDiners = false;
    }

    OnDinersOther(event){
        let number = Number(event.trim());
        let size = (Number.isInteger(number)) ? number: null;

        this._ticket.diners = size;
    }

    /***********************************/
    /* STARTS HERE                     */
    /***********************************/

    private _ticketchanges = {
        toInsert: [],
        toUpdate: [],
        toDelete: [],
        toModify: []
    };

    constructor(private lang: languageService, private change: ChangeDetectorRef, private toast: toastService, private alertCtrl: alertService, private modalCtrl: ModalController, private sync: syncService, private data: dataService, public view: viewService, private devsvc: deviceService, private cashlessStore: cashlessRefundStore) {
        super(lang, change);
    }

    private _minute_subscription = null;
    private _theme_subscription = null;

    ngOnInit(){
        this._minute_subscription = this.sync.Clock.OnOneSecondTick.subscribe(
        data => {
            this._checkDiscountStatus();            
        });

        this._theme_subscription = this.view.OnTheme.subscribe(
        data => {
            this.change.markForCheck();
        });
    }

    private _RefreshTicket(){
        this._discounts = null;
        this.change.detectChanges();
    }

    private _continious = false;    // ticket continious mode
    ngOnChanges(){
        this._ticket.isopen = true;
        this._notes = this._ticket.comments;
        this._tabletickets = null;
        this._paidElsewhereNotified = false;
        this._paidElsewherePending = false;
        this._localPayment = false;
        // remember if the ticket was already paid when the editor opened
        // (cancel/refund flow): only fire the banner on a fresh false→true transition
        this._initialIsPaid = !!(this._ticket?.CopyOf as Ticket)?.IsPaid;

        // Mesa original al abrir. Si CopyOf pasa a otra mesa (movida/juntada) o
        // deja de ser valida (rechazada/eliminada) desde OTRO dispositivo,
        // cerramos el editor con aviso (ver _CheckChangedElsewhere).
        this._changedElsewhereNotified = false;
        this._changedElsewherePending = false;
        this._localCancel = false;
        this._initialQrcode = (this._ticket?.CopyOf as Ticket)?.qrcode?.objid || null;
        this._initialStatus = (this._ticket?.CopyOf as Ticket)?.status || null;
        this._baselineProductsSig = this._CopyOfProductsSig();
        this._localCommitInFlight = false;

        this._ticketchanges = {
            toInsert: [],
            toUpdate: [],
            toDelete: [],
            toModify: []
        };

        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];

        // Marca de commit local: arma la flag para que _CheckChangedElsewhere no
        // confunda el avance de `updated` provocado por MI propio commit con un
        // cambio externo. Nos suscribimos a OnCommit de AMBOS: la COPIA
        // (this._ticket) y el ORIGINAL (CopyOf) — en comandas existentes el editor
        // aplica los cambios sobre el ORIGINAL y hace DoCommit ahi
        // (_ApplyChanges(_copyof)), asi que OnCommit emite en CopyOf, no en la
        // copia. Un cambio REMOTO llega por Data/OnChange (no por OnCommit), asi
        // que nunca arma la flag.
        //
        // La flag se DESARMA cuando commitReady (del payload de OnCommit) resuelve:
        // el commit se confirmo y CopyOf.updated ya refleja MI cambio, asi que
        // re-baselineamos ahi. No dependemos del '>' del detector para consumirla
        // — `updated` tiene resolucion de segundos (mysqlToDateStr fuerza .000Z),
        // asi que un commit en el mismo segundo que la base no cruzaria el '>' y la
        // flag quedaria armada indefinidamente comiendose un cambio remoto posterior.
        if (this._ticket){
            let _markLocalCommit = (payload: any) => {
                this._localCommitInFlight = true;
                let _ready = payload && payload.commitReady;
                if (_ready && typeof _ready.then === 'function'){
                    // Capturar el ticket actual: commitReady puede resolver DESPUES
                    // de que ngOnChanges cambie el editor a otra comanda (la sub de
                    // OnCommit se limpia, pero la promesa ya adjunta no). Sin este
                    // guard el callback mutaria baseline/flag de OTRO ticket.
                    let _forTicket = this._ticket;
                    _ready.then(() => {
                        if (this._ticket !== _forTicket){ return; }
                        this._baselineProductsSig = this._CopyOfProductsSig();
                        this._localCommitInFlight = false;
                    }).catch(() => {
                        if (this._ticket !== _forTicket){ return; }
                        this._localCommitInFlight = false;      // commit fallo: desarmar
                    });
                }
            };
            this._subscriptions.push(this._ticket.OnCommit.subscribe(_markLocalCommit));
            let _copyof = this._ticket.CopyOf as Ticket;
            if (_copyof){
                this._subscriptions.push(_copyof.OnCommit.subscribe(_markLocalCommit));
            }
        }

        if (this._place){
            this._subscriptions.push(
                this._place.OnTargetRefresh('TICKET').subscribe(
                data => {
                    this._tabletickets = null;
                    this._discounts = null;
                    setTimeout(() => {
                        if (this._CheckPaidElsewhere()){
                            return;     // editor closed: do not recalculate
                        }
                        if (this._CheckChangedElsewhere()){
                            return;     // movida/juntada/rechazada en otro dispositivo
                        }
                        this._ticket.TicketControl.DoUpdate(true);
                        this.change.detectChanges();
                    }, 500);
                })
            );
        }

        if (this._ticket){
            this._continious = false;
            if (this._ticket.qrcode){
                this._subscriptions.push(
                    this._ticket.qrcode.OnRefresh.subscribe(
                    data => {
                        this._tabletickets = null;
                        this.change.markForCheck();
                    })
                );   
            }
            else {
                this._continious = this._place.IsPOSDevice && (this._ticket.status == 'PR');;
            }

            this._subscriptions.push(
                this._ticket.OnProducts.subscribe(
                data => {
                    this.ScrollView();
                })
            )

            this._subscriptions.push(
                this._ticket.OnModify.subscribe(
                data => {
                    this.AddChange(data);
                })
            )

            this._subscriptions.push(
                this._ticket.OnRefresh.subscribe(
                data => {
                    this._RefreshTicket();
                })
            );
        }
    }

    private get CanModify(){
        return this._place.AllowTicketUpdate(this._user);
    }

    ngAfterViewInit(){
        if (this._ticket.BarTicket){
            return;     // nothing to do
        }

        // show table configuration on error
        if (this._ticket.qrcode.IsError){
            this._onPanel.emit({
                scnd: 'opts',
                args: {
                    qrcode: this._ticket.qrcode
                }
            });

            return;
        }

        // edit the ticket if only one unpaid ticket is present
        if (this.CanModify && (this.TableTickets.length == 1)){

            // If we're already editing a COPY of this same ticket (re-entry
            // into the editor after navigating away), DO NOT recreate the
            // COPY: that would wipe the cashier's pending edits. This shows
            // up in datafono after a paytef cancel — the cashier added 6 €
            // of products, paytef denied/cancelled, navigated back to the
            // cart and re-entered the editor; without this guard the COPY
            // gets replaced by a fresh one of the (unchanged) original and
            // the 6 € of edits vanish silently.
            if (this._ticket.CopyOf == this.TableTickets[0]){
                return;
            }

            // Defensa: nunca auto-editar un ticket ya pagado (QR PD + productos
            // AC pendientes de V). TableTickets ya los excluye; por si acaso.
            if (this.TableTickets[0].IsPaid){
                return;
            }

            let _oldcart = this._ticket;
            let _newcart = this.TableTickets[0].Copy();
            if (_newcart){
                this._place.DelCart(_oldcart);
                this._place.AddCart(_newcart);
            }

            this._onPanel.emit({
                main: 'cart',
                scnd: null,
                args: {
                    ticket: _newcart
                }
            });

            return;
        }
    }

    ngOnDestroy(){
        super.OnDestroy();

        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];
        
        if (this._theme_subscription){
            this._theme_subscription.unsubscribe();
            this._theme_subscription = null;
        }

        if (this._minute_subscription){
            this._minute_subscription.unsubscribe();
            this._minute_subscription = null;
        }

        this._ticket.isopen = false;
    }

    /**********************************/
    /* CHANGES CONTROL                */
    /**********************************/

    public _notes = null;

    private AddChange(change){
        if (change.operation == 'add'){
            this._ticketchanges.toInsert.push(change.product);
        }

        if (change.operation == 'set'){
            if (change.product){
                this._ticketchanges.toUpdate.push(change.product);
            }
        }

        // 'mod' (MODIFICAR-N / CartModify): modificacion de un producto ya
        // comandado. Se reproduce al ticket original via CartMod (no CartUdt)
        // para que llegue a cocina como MODIFICADO, sin par DE+AC.
        if (change.operation == 'mod'){
            if (change.product){
                this._ticketchanges.toModify.push(change.product);
            }
        }

        if (change.operation == 'del'){
            let _idx = this._ticketchanges.toInsert.indexOf(change.product);
            if (_idx != -1){
                this._ticketchanges.toInsert.splice(_idx, 1);
            }
            else {
                this._ticketchanges.toDelete.push(change.product);
            }
        }

        this._RefreshTicket();
    }

    @ViewChild('scrollable', { static: false }) _scrollable: ElementRef; 
    @ViewChild('lstproduct', { static: false }) _lstproduct: ElementRef; 

    private ScrollView(){
        let _escroll = this._scrollable?.nativeElement; 
        let _element = this._lstproduct?.nativeElement;
        if (_escroll && _element){
            _escroll.scrollTo({
                top: _element.offsetTop + _element.offsetHeight - _escroll.clientHeight + 300,
                behavior: 'smooth'
              });            
        }
    }

    /***********************************/
    /* EVENT HANDLERS                  */
    /***********************************/

    private get IsPristine(){
        let _copyof = this._ticket.CopyOf as Ticket;
        if (_copyof){
            let _pristine = true;
            
            _pristine = _pristine && (_copyof.qrcode == this._ticket.qrcode);
            _pristine = _pristine && (this._ticket.comments == this._notes);
            _pristine = _pristine && (_copyof.diners == this._ticket.diners);

            // check ticket product changes
            _pristine = _pristine && (_copyof.products.length == this._ticket.products.length);
            if (_pristine){
                _pristine = this._ticket.products.every(
                (_product) => {
                    let _copyof = _product.CopyOf as TicketProduct;
                    if (_copyof){   // check product update (status && options)
                        if (_product.status != _copyof.status){
                            return false;
                        }

                        if (_product.sort != _copyof.sort){
                            return false;
                        }

                        let _pristine =  (_product.options.length == _copyof.options.length);
                        if (_pristine){
                            _pristine = _product.options.every(
                            (_option) => {
                                let _copyof = _option.CopyOf as TicketOption;
                                if (_copyof){
                                    if ((_option.product.CopyOf || _option.product) != _copyof.product){
                                        return false;
                                    }

                                    if ((_option.option.CopyOf || _option.option) != _copyof.option){
                                        return false;
                                    }

                                    return true;    // same product and option
                                }

                                return false;   // this is a new option
                            })
                        }

                        return _pristine;
                    }

                    return false;        // this is a new product
                })
            }

            // check ticket discounts changes
            _pristine = _pristine && (_copyof.discounts.length == this._ticket.discounts.length);
            if (_pristine){
                _pristine = this._ticket.discounts.every(
                (_discount) => {
                    let _copyof = _discount.CopyOf as TicketDiscount;
                    if (!_copyof){
                        return false;   // this is a new discount 
                    }
                    else {  // check discount update (discount && status)
                        if (_discount.discount != _copyof.discount){
                            return false;
                        }

                        if (_discount.status != _copyof.status){
                            return false;
                        }
                    }

                    return true;    // same discount and status
                });
            }

            this._ticket['__pristine'] = _pristine;
            return _pristine;
        }
        else {  // newly created ticket
            return (this._ticket.products.length == 0);
        }
    }

    private _OnExit(_ticket){
        let _args = null;
        if (_ticket){
            _args = {
                ticket: _ticket
            };
        }

        this._onPanel.emit({
            main: 'view',
            scnd: null,
            args: _args,
            sync: () => {
                this._place.DelCart(this._ticket);
            }
        });
    }

    /**
     * Close editor without the "changes will be lost" dialog.
     * Used after user confirmation and also when force-closing
     * because the ticket was paid on another terminal.
     */
    private _CloseEditorWithoutConfirmDialog(){
        if (this._mergeinfo){
            this.OnMergeCancel();
        }

        // Unified exit for mobile + datáfono + desktop: the cashier clicked
        // X (close) with either no pending changes or after confirming the
        // discard. Expected behaviour is 'leave the editor and go back to
        // the mesas/place view'. The earlier mobile-only branch emitted
        // main:'cart' which landed the user on ui-ticket-select (the
        // product grid) and felt like a back-arrow press instead of a
        // close. The back arrow already owns that 'cart' navigation; X
        // must escalate to a full close.
        //
        // OnCancel() handles the 'new ticket never touched' case: DoCancel
        // marks the empty-new ticket as cancelled server-side so it doesn't
        // linger, then calls _OnExit(null) itself. _OnExit emits
        // main:'view' and runs DelCart via its sync callback so the draft
        // is dropped after the navigation lands.
        if (!this._ticket.CopyOf && this._ticket.ToInsert){
            this.OnCancel();
        }
        else {
            this._OnExit(null);
        }
    }

    private _OnClose(){
        this._CloseEditorWithoutConfirmDialog();
    }

    private _paidElsewhereNotified = false;
    private _paidElsewherePending = false;
    private _localPayment = false;
    private _initialIsPaid = false;

    private _changedElsewhereNotified = false;
    private _changedElsewherePending = false;
    private _localCancel = false;
    private _initialQrcode: string | null = null;
    private _initialStatus: string | null = null;
    // Linea base de la FIRMA de productos de CopyOf al abrir y tras cada commit
    // local. Si cambia y NO fue un commit de ESTE editor -> otro terminal ANADIO
    // o QUITO lineas. Se usa el contenido (conjunto de refid) y NO `updated`: el
    // timestamp no se propaga cuando otro terminal manda. Solo detecta ALTA/BAJA
    // de productos, NO edicion in-place de una linea existente (mismo _uuid:
    // cambiar opciones/comentarios/cantidad de un producto ya presente) — el caso
    // dominante del TPV es anadir/quitar. Cubre lo que qrcode (mover) y status
    // (rechazar/cancelar) no detectan.
    private _baselineProductsSig: string | null = null;
    private _localCommitInFlight = false;

    /**
     * Check if the original ticket (CopyOf) has been paid by another terminal.
     *
     * When the editor opens a ticket for modification it works on a shallow copy;
     * CopyOf points to the live instance that sync keeps up-to-date.  If that
     * instance transitions to IsPaid, the editor must close — continuing would
     * produce a spurious refund commit against an already-paid ticket.
     *
     * Skipped when _localPayment is true (this terminal initiated the payment)
     * or when there is no CopyOf (new ticket with no original to compare).
     *
     * Confirmación de dos pasos: un write en vuelo rescatado tras un sleep/lock
     * del dispositivo (visibilitychange: bloqueo de sesión, pestaña en background
     * >60s, suspensión — no es exclusivo de iOS) puede re-marcar el ticket como
     * pagado de forma TRANSITORIA (el estado autoritativo del server sigue en AC
     * tras reabrir). No cerramos al primer IsPaid=true: armamos una
     * confirmación pendiente y solo cerramos si el
     * estado pagado PERSISTE en el siguiente refresh, que ya refleja el pull
     * autoritativo del servidor. Si el segundo refresh lo ve NO pagado, era un
     * pico obsoleto y desarmamos sin cerrar.
     */
    private _CheckPaidElsewhere(immediate = false): boolean {
        if (this._localPayment){
            return false;
        }

        // ticket was already paid when the editor opened (cancel/refund flow):
        // do not fire the banner — the user knowingly opened a paid ticket
        if (this._initialIsPaid){
            return false;
        }

        let _copyof = this._ticket?.CopyOf as Ticket;
        if (!_copyof || !_copyof.IsPaid){
            this._paidElsewherePending = false;    // pico transitorio: desarmar
            return false;
        }

        // Pagado por ESTA sesion (este mismo dispositivo): al cobrar, el setter
        // de payment pasa status a 'PD' y estampa paidin = data.session ANTES
        // del commit; sync lo propaga en crudo (DoPatchValues no pasa por el
        // setter, nadie lo pisa con su propia sesion). Si la sesion que cobro
        // es la nuestra NO es "pagado en otro dispositivo": ni avisar ni cerrar.
        // A diferencia de _localPayment (que ngOnChanges resetea y se arma
        // tarde, tras el modal de pago), paidin no depende del timing.
        // paidby cubre el pago a cuenta ('PP'), que estampa paidby y no paidin.
        let _paidSession = _copyof.paidin || _copyof.paidby;
        let _ownSession = this.data.session;
        if (_paidSession && _ownSession &&
            ((_paidSession === _ownSession) ||
             (_paidSession.objid && (_paidSession.objid == _ownSession.objid)))){
            this._paidElsewherePending = false;
            return false;
        }

        if (this._paidElsewhereNotified){
            return true;
        }

        // primer refresh con IsPaid=true: esperar confirmación del server.
        // immediate=true (OnCommit) salta este paso y actúa al instante.
        if (!immediate && !this._paidElsewherePending){
            this._paidElsewherePending = true;
            return true;    // gate el recálculo, pero NO cerrar todavía
        }

        this._paidElsewherePending = false;
        this._paidElsewhereNotified = true;

        this.toast.ShowAlert('warning', this.lang.tr('@ticket_paid_elsewhere'), 3000);
        this._CloseEditorWithoutConfirmDialog();
        this.change.detectChanges();
        return true;
    }

    // Firma del CONTENIDO de CopyOf = conjunto de refid (_uuid) de sus productos.
    // El contenido SI se propaga cuando otro terminal edita y manda (a diferencia
    // de `updated`, que no llega porque el cliente envia su propio timestamp). Es
    // pariente de la senal que enciende ACTUALIZAR, pero NO identica: Ticket._OnUpdate
    // compara el `hash` de CADA producto (Map por instancia), asi que ademas del
    // alta/baja detecta ediciones in-place de una linea; esta firma solo mira el
    // conjunto de refid -> solo caza ALTA/BAJA.
    // Se usa refid y NO objref/hash porque ambos cambian al pasar un producto propio
    // de optimista a persistido, disparando un falso cierre al mandar y reabrir en el
    // MISMO terminal: `objref` es objid|_uuid, asi que flip _uuid->objid; `hash`
    // excluye el objid propio (base.ts) pero EMBEBE la ref al ticket padre, que hace
    // la misma transicion -> tambien cambia (verificado en traza). El `_uuid`/refid
    // es lo unico ESTABLE (la fila persistida lo conserva).
    private _CopyOfProductsSig(): string | null {
        let _copyof = this._ticket?.CopyOf as Ticket;
        let _products = _copyof && _copyof.products;
        if (!_products){
            return null;
        }
        return _products
            .map((_p) => _p.refid || '')
            .sort()
            .join('|');
    }

    /**
     * Cierra el editor si la comanda original (CopyOf) fue movida/juntada a otra
     * mesa o rechazada/eliminada por OTRO dispositivo. Mismo patron que
     * _CheckPaidElsewhere: confirmacion en 2 pasos para no cerrar por un pico
     * transitorio (write obsoleto rescatado tras un sleep/lock del dispositivo).
     *
     * El guard `this._ticket.qrcode?.objid == _initialQrcode` evita cerrar por una
     * accion LOCAL: si el propio editor movio la comanda, su qrcode ya refleja
     * la mesa nueva y no se dispara.
     */
    private _CheckChangedElsewhere(immediate = false): boolean {
        // _localPayment / _localCancel: el cambio lo inicio ESTE terminal (pago o
        // cancelacion/rechazo local) -> no es "cambiado en otro dispositivo".
        if (this._localPayment || this._localCancel){
            return false;
        }

        let _copyof = this._ticket?.CopyOf as Ticket;
        if (!_copyof){
            return false;   // ticket nuevo sin original: nada que comparar
        }

        // movida/juntada a otra mesa: CopyOf cambio de qrcode pero el editor sigue
        // en la mesa original (el cambio vino de fuera, no de aqui).
        let _movedAway = (_copyof.qrcode?.objid != this._initialQrcode) &&
                         (this._ticket.qrcode?.objid == this._initialQrcode);
        // rechazada / eliminada / juntada (origen dado de baja): deja de ser valida.
        // rechazada / cancelada / eliminada / juntada (origen dado de baja) desde
        // OTRO dispositivo: CopyOf paso a un estado terminal (CC/DE) que no tenia
        // al abrir, y el editor sigue en un estado activo (el cambio vino de fuera).
        // Cubre tambien el cancelar-con-reembolso de una pagada (CC + ToRefund),
        // que IsActive/IsValid no detectan (IsActive de CC = !!ToRefund = true).
        let _cancelledElsewhere = (['CC', 'DE'].indexOf(_copyof.status) != -1) &&
                                  (_copyof.status != this._initialStatus) &&
                                  (['CC', 'DE'].indexOf(this._ticket.status) == -1);
        let _goneAway = !_copyof.IsValid || _cancelledElsewhere;

        // Otro terminal ANADIO o QUITO lineas (dividir, juntar-lado-receptor,
        // anadir producto desde otra mesa): la FIRMA de productos de CopyOf cambia
        // respecto a la linea base. Se usa el CONTENIDO y NO `updated`: el timestamp
        // no se propaga cuando otro terminal manda (el cliente envia su propio
        // `updated`, por eso antes NO cerraba), pero los productos SI llegan — es la
        // misma senal que enciende ACTUALIZAR (Ticket._OnUpdate / _lastproducts).
        // OJO: la firma es el conjunto de refid, asi que solo caza ALTA/BAJA de
        // lineas; una edicion in-place de una linea existente en otro terminal
        // (mismo _uuid, cambio de opciones/comentarios) NO cierra — asumido, el
        // caso real del TPV es anadir/quitar.
        //
        // Mientras hay un commit local en vuelo (_localCommitInFlight) NO se trata
        // como externo: el re-baseline de la firma lo hace SOLO commitReady tras
        // confirmar el commit propio. Asi, mandar una comanda y reabrirla en el
        // MISMO terminal NO cierra (la firma no cambia entre commits propios), y
        // que otro terminal anada/quite una linea SI cierra (la firma cambia sin
        // commit local).
        let _contentChangedElsewhere = false;
        let _nowSig = this._CopyOfProductsSig();
        if ((_nowSig != null) && (this._baselineProductsSig != null) &&
            (_nowSig !== this._baselineProductsSig) && !this._localCommitInFlight){
            _contentChangedElsewhere = true;
        }

        if (!_movedAway && !_goneAway && !_contentChangedElsewhere){
            this._changedElsewherePending = false;    // pico transitorio: desarmar
            return false;
        }

        if (this._changedElsewhereNotified){
            return true;
        }

        // primer refresh con el cambio: esperar confirmacion del server (2 pasos).
        // immediate=true (OnCommit) salta este paso y cierra al instante.
        if (!immediate && !this._changedElsewherePending){
            this._changedElsewherePending = true;
            return true;    // gate el recalculo, pero NO cerrar todavia
        }

        this._changedElsewherePending = false;
        this._changedElsewhereNotified = true;

        this.toast.ShowAlert('warning', this.lang.tr('@ticket_changed_elsewhere'), 3000);
        this._CloseEditorWithoutConfirmDialog();
        this.change.detectChanges();
        return true;
    }

    @Output('onPanel') _onPanel = new EventEmitter<any>();     
    OnBack(){
        this._onPanel.emit({
            main: 'cart',
            scnd: null,
            args: null
        });
    }

    async OnClose(){
        if (!this.IsPristine){  // check if products list is updated
            console.log("[EDIT TICKET] confirm changes lost");
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@changes_lost_confirm_tittle'),
                message: this.lang.tr('@changes_lost_confirm_message'),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);
    
                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);
                        return false;
                    }
                }]
            });
    
            alert.onDidDismiss().then((detail:any) => {
                if (detail.data == true){
                    this._OnClose();
                }
            });
    
            await alert.present();    
        }
        else {
            this._OnClose();
        }    
    }

    private _onshowlast = false;
    get ShowLast(){
        return this._onshowlast;
    }

    set ShowLast(value){
        this._onshowlast = value;
        this.change.markForCheck();
    }

    OnLast(){
        this.ShowLast = !this.ShowLast;
    }

    OnPanel(panel){
        // if panel is 'cart' perform a ticket change
        if (panel.main == 'cart'){  
            let _ticket = panel.args.ticket;
            this._place.DelCart(this._ticket);

            if (_ticket){
                this._place.AddCart(_ticket);
                this._ticket = _ticket;
            }

            this.SelectTicket(null);
        }

        this._onPanel.emit(panel);
    }

    private _ReparentProduct(_ticketproduct, _ticket){
        _ticketproduct.offer = null;

        _ticketproduct.ticket = _ticket;
        if (_ticketproduct.CopyOf){
            // Overwrite (swap copia->original) PIERDE las TicketOption recreadas
            // por MODIFICAR-N al reabrir: son nuevas (sin CopyOf), asi que su
            // propio Overwrite hace early-return y no se re-adjuntan -> el
            // producto queda con options=0 -> TotalPrice=0 -> ChargePrice=0 (el
            // "producto pagado que se muestra a 0" y entra a 0 en el TicketBai).
            // Capturamos las ProductOption + el estado de precio ANTES y, si
            // Overwrite las perdio, las re-aplicamos (mismo patron que CartModify).
            let _opts = _ticketproduct.options.map(_o => _o.option).filter(Boolean);
            let _fixed = _ticketproduct.fixed;
            let _charge = _ticketproduct.charge;

            _ticketproduct.Overwrite();

            if (_ticketproduct.options.length < _opts.length){
                for (let _o of [..._ticketproduct.options]){
                    _ticketproduct.DelOption(_o);
                }
                for (let _o of _opts){
                    _ticketproduct.CartAdd(_o);
                }
                _ticketproduct.fixed = _fixed;
                _ticketproduct.price = _ticketproduct.TotalPrice;
                _ticketproduct.charge = _fixed ? _charge : _ticketproduct.ChargePrice;
            }
        }

        return _ticketproduct;
    }

    private _ReparentDiscount(_ticketdiscount, _ticket){
        _ticketdiscount.ticket = _ticket;
        if (_ticketdiscount.CopyOf){
            _ticketdiscount.Overwrite();
        }

        return _ticketdiscount;
    }

    private _ApplyChanges(_ticket: Ticket, _ticketdiscounts, _doupdate = true){
        _ticket.status = this._ticket.status;
        _ticket.diners = this._ticket.diners;

        let _account = this._ticket.account;
        if (_ticket.account != _account){
            _ticket.account = _account;
        }

        let _given = this._ticket.given;
        if (_ticket.given != _given){
            _ticket.given = _given;
        }

        let _qrcode = this._ticket.qrcode
        if (_ticket.qrcode != _qrcode){
            let _oldtable = _ticket.qrcode;
            if (_oldtable){
                _oldtable.DelTicket(_ticket);
            }
            
            _ticket.qrcode = _qrcode;
            if (_qrcode){
                this._ticket.qrcode.AddTicket(_ticket);
            }
        }

        for (let _mixedpayment of this._ticket.mixed){
            let _newmixed = null;
            for (let _oldmixed of _ticket.mixed){
                if (_oldmixed.payment == _mixedpayment.payment){
                    _newmixed = _oldmixed;
                }
            }
            
            _newmixed = _newmixed || _mixedpayment;
            if (_newmixed){
                _newmixed.ticket = _ticket;
                _newmixed.amount = _mixedpayment.amount;
            }

            _ticket.AddMixed(_newmixed);
        }

        for(let _ticketproduct of this._ticketchanges.toInsert){
            _ticket.CartAdd(this._ReparentProduct(_ticketproduct, _ticket));
        }

        for(let _ticketproduct of this._ticketchanges.toUpdate){
            _ticket.CartUdt(this._ReparentProduct(_ticketproduct, _ticket));
        }

        for(let _ticketproduct of this._ticketchanges.toModify){
            _ticket.CartMod(this._ReparentProduct(_ticketproduct, _ticket));
        }

        for(let _ticketproduct of this._ticketchanges.toDelete){
            _ticket.CartDel(this._ReparentProduct(_ticketproduct, _ticket));
        }

        for(let _ticketdiscount of _ticketdiscounts){
            _ticket.AddDiscount(this._ReparentDiscount(_ticketdiscount, _ticket));
        }

        if (_doupdate){
            _ticket.isopen = true;
            _ticket.DoUpdate(true)
            _ticket.isopen = false;
        }

        // clear pending changes: products were reparented into _ticket, so
        // a subsequent _ApplyChanges call (e.g. after a denied paytef payment
        // when the editor stays open and the user retries) MUST NOT re-apply
        // the same products into the original ticket again — that would
        // accumulate ghost products / inflate the amount on every retry.
        this._ticketchanges.toInsert = [];
        this._ticketchanges.toUpdate = [];
        this._ticketchanges.toModify = [];
        this._ticketchanges.toDelete = [];

        return _ticket;
    }

    async OnCancel(){
        let _ticket = this._ticket;

        // if ticket is a copy (it is a modification, then apply changes)
        let _copyof = _ticket.CopyOf as Ticket;

        // Mismo guard que OnCommit, ANTES de aplicar nada: cancelar aplica la copia
        // sobre el ORIGINAL vivo y lo cancela ENTERO, incluidas las lineas que otro
        // dispositivo haya anadido y que este editor nunca mostro. Caso real (Mandarin,
        // 12-oct): un movil con la copia sin los 3 cafes que otra camarera acababa de
        // anadir cancelo la mesa y se borraron tambien esos cafes. Y si otro terminal
        // ya la cobro, cancelar aqui anularia un ticket pagado. La deteccion de fondo
        // espera 2 refrescos antes de cerrar el editor; en ese hueco el cajero puede
        // pulsar Eliminar. immediate=true: es una accion explicita -> abortar, avisar y
        // cerrar; al reabrir se ve la comanda actual y se puede cancelar a sabiendas.
        // Sin copia (comanda nueva que se descarta al cerrar) no hay nada que comparar.
        if (_copyof){
            if (this._CheckPaidElsewhere(true) || this._CheckChangedElsewhere(true)){
                return;
            }
        }

        if (_copyof){
            _ticket = this._ApplyChanges(_copyof, _ticket.discounts);
        }

        let _commit = ((_ticket.status == 'PR') && this.IsPristine);
        if (!_commit){
            this._localCancel = true;   // cancelacion local: no disparar changed-elsewhere
            _ticket.DoCancel();
            _ticket.DoCommit().then(
            success => {
                if (success){
                    this.toast.ShowAlert('danger', this.lang.tr('@place_ticket_cancelled_toast'), 2000);
                }
            });    
        }

        this._OnExit(null);
    }

    private _waitmodal = false;
    // Pago mixto: ¿el efectivo entro REALMENTE en el cajon? Solo entonces se
    // puede devolver por CASH_OUT si luego falla la tarjeta. En fallback a
    // efectivo manual o sin cajon el dinero NO esta en la maquina (un CASH_OUT
    // dispensaria saldo ajeno). Lo setea _ProcessMixedCashPayment.
    private _mixedCashInDrawer = false;
    private OnPaycash(_ticket){
        // Antes exigia IsRecent (<8h): un pedido viejo se marcaba pagado SIN
        // abrir el cajon/datafono. Ahora cualquier ticket sin pagar cobra por la
        // maquina; solo se salta si el local no tiene modal de efectivo.
        if (!this._place.CashModal){
            return true;
        }

        return new Promise<boolean>(async (resolve) => {
            if (!this._waitmodal){
                this._waitmodal = true;
                
                // Resuelve a que CashGuard mandar el cobro (per-ticket flow).
                let _cgInfo = this._place.smartdrawerForTicket(this._ticket);
                let _smartdrawer = _cgInfo?.drawer || null;
                let _cgTill = _cgInfo?.till || null;
                let _tolocal = _cgInfo?.tolocal ?? false;

                let modal;
                if (_smartdrawer && _cgTill){
                    // Present immediately — connection/STATUS runs inside the modal.
                    modal = await this.modalCtrl.create({
                        component: UiCashGuardCashPayModal,
                        cssClass: 'modal-cashguard',
                        backdropDismiss: false,
                        componentProps: {
                            tickets: [ _ticket ],
                            device: _smartdrawer,
                            place: _cgTill,
                            tolocal: _tolocal
                        }
                    });
                } else {
                    // Use regular cash modal
                    modal = await this.modalCtrl.create({
                        component: UiCashPayModal,
                        cssClass: 'modal-cash',
                        backdropDismiss: false,
                        componentProps: { 
                            tickets: [ _ticket ]
                        }                                    
                    });
                }
    
                modal.onDidDismiss().then(async (detail) => {
                    // Check if CashGuard modal requested fallback to cash payment
                    if (detail.data && detail.data['fallback']){
                        console.log('[SMARTDRAWER] Fallback to cash payment modal requested');
                        // Open regular cash modal as fallback
                        let cashModal = await this.modalCtrl.create({
                            component: UiCashPayModal,
                            cssClass: 'modal-cash',
                            backdropDismiss: false,
                            componentProps: { 
                                tickets: [ _ticket ]
                            }                                    
                        });
                        
                        cashModal.onDidDismiss().then((cashDetail) => {
                            if (cashDetail.data){
                                _ticket.given = cashDetail.data['amount'];
                            }
                            resolve(cashDetail.data != null);
                        });
                        
                        await cashModal.present();
                    } else {
                        if (detail.data){
                            _ticket.given = detail.data['amount'];
                        }
                        resolve(detail.data != null);
                    }
                });
    
                await modal.present();
                
                setTimeout(() => {
                    this._waitmodal = false;
                }, 100);
            }
        });
    }

    // Deps que necesita el helper cashless (cobro tarjeta por el datafono del
    // cajon) y que no son globales.
    private _cashlessDeps(amountDue?: number): CashlessModalDeps {
        return {
            modalCtrl: this.modalCtrl,
            cashlessStore: this.cashlessStore,
            hasCardModal: !!this._place.CardModal,
            openManual: (tk) => this._openManualCardModal(tk, amountDue),
            amountDue: amountDue,
            // Liberar el guard al cerrarse la modal cashless (antes del fallback
            // manual), para que _openManualCardModal — que corre dentro del
            // onDidDismiss del helper — no se auto-bloquee con _waitmodal.
            onModalDismissed: () => { this._waitmodal = false; }
        };
    }

    private async OnPaycard(_ticket){
        // Autorizar ANTES de cobrar por tarjeta: si es una modificación sin permiso,
        // no arrancamos (mismo motivo que OnMixedPayment: no cobrar y abortar después).
        if (this.IsTicketUpdate && !this._place.AllowTicketUpdate(this._user)){
            return false;
        }
        // Antes exigia IsRecent (<8h) y marcaba pagado el ticket viejo SIN pasar
        // por el datafono. Ahora cualquier ticket sin pagar cobra por la maquina
        // (cae a manual/CardModal si no hay terminal).

        let _cgInfo = this._place.smartdrawerForTicket(_ticket);

        // Selector de datáfono UNIFICADO (helper compartido): si hay datáfonos de
        // AMBOS tipos (PROSEGUR cashless + PAYTEF por IP), deja elegir al cajero.
        // Con un solo tipo devuelve 'default' y sigue el flujo de siempre.
        // Guard anti doble-tap DURANTE el selector (ver ui-view): dos toques
        // rápidos abrían dos pickers / dos cobros. Se libera al cerrarse el picker.
        if (this._waitmodal){ return false; }
        this._waitmodal = true;
        let _choice;
        try {
            _choice = await chooseCardTerminal(this.modalCtrl, this.lang, this._place, this.view, _cgInfo);
        } finally {
            this._waitmodal = false;
        }
        if (_choice.kind !== 'default'){
            if (_choice.kind === 'cancelled'){ return false; }
            if (_choice.kind === 'manual'){
                if (!this._place.CardModal){ return true; }
                return this._openManualCardModal([ _ticket ]);
            }
            if (_choice.kind === 'paytef'){
                return this._openPaytefModal([ _ticket ], _choice.terminal);
            }
            // prosegur: cobro cashless por el datáfono elegido.
            if (this._waitmodal){ return false; }
            let _deps = this._cashlessDeps();
            _deps.chosenTerminal = _choice.terminal;
            this._waitmodal = true;
            let _r: boolean | null;
            try {
                _r = await tryCashlessCardPayment(this.devsvc, _deps, [ _ticket ], _cgInfo);
            } finally {
                this._waitmodal = false;
            }
            return _r === null ? false : _r;
        }

        // Cajon con datafono integrado (PROSEGUR / CASHLOGY): cobramos la
        // tarjeta por el (CHARGE_CASHLESS) en vez de por paytef. Si el cajon
        // no puede con tarjeta, devuelve null y seguimos al flujo normal.
        //
        // Guard _waitmodal (el mismo que los demas modales) mantenido durante
        // TODO el intento cashless: resuelve al cerrarse la modal, asi un 2o
        // toque mientras se cobra la tarjeta no abre una segunda modal (doble
        // cargo). Se libera antes de caer al flujo paytef, que lo retoma.
        if (this._waitmodal){
            return false;
        }
        this._waitmodal = true;
        let _cgCard: boolean | null;
        try {
            _cgCard = await tryCashlessCardPayment(this.devsvc, this._cashlessDeps(), [ _ticket ], _cgInfo);
        } finally {
            this._waitmodal = false;
        }
        if (_cgCard !== null){
            return _cgCard;
        }

        // Paytef is independent from CardModal: if a terminal is configured
        // we always route the payment through its modal.
        let _terminals = resolvePaymentTerminals(this._place, this.view);

        if (_terminals.length > 0) {
            let _pick = await pickPaymentTerminal(this.modalCtrl, this.lang, _terminals, this.view);
            if (!_pick){
                return false;   // user cancelled the picker
            }
            if (isPaymentTerminalManual(_pick)){
                if (!this._place.CardModal){
                    return true;
                }
                return this._openManualCardModal([ _ticket ]);
            }
            return this._openPaytefModal([ _ticket ], _pick);
        }

        if (!this._place.CardModal){
            return true;
        }

        return new Promise<boolean>(async (resolve) => {
            if (!this._waitmodal){
                this._waitmodal = true;
                const modal = await this.modalCtrl.create({
                    component: UiCardPayModal,
                    cssClass: 'modal-card',
                    backdropDismiss: false,
                    componentProps: {
                        tickets: [ _ticket ]
                    }
                });

                modal.onDidDismiss().then((detail) => {
                    resolve(detail.data);
                });

                await modal.present();

                setTimeout(() => {
                    this._waitmodal = false;
                }, 100);
            }
        })
    }

    private async _openPaytefModal(tickets, terminal, saleAmount?: number): Promise<boolean> {
        return new Promise<boolean>(async (resolve) => {
            if (this._waitmodal){
                resolve(false);   // modal en vuelo: no colgar al caller
                return;
            }
            this._waitmodal = true;
            try {
                let _props: any = {
                    tickets: tickets,
                    terminal: terminal,
                    place: this._place
                };
                if (typeof saleAmount === 'number' && saleAmount > 0){
                    _props.saleAmount = saleAmount;
                }
                const modal = await this.modalCtrl.create({
                    component: UiPaytefPayModal,
                    cssClass: 'modal-paytef',
                    backdropDismiss: false,
                    componentProps: _props
                });

                modal.onDidDismiss().then((detail) => {
                    resolve(detail.data && (detail.data.approved || detail.data.manual) ? true : false);
                });

                await modal.present();
            } catch(_e){
                console.error('[PAY] paytef modal error', _e);
                resolve(false);
            } finally {
                setTimeout(() => { this._waitmodal = false; }, 100);
            }
        });
    }

    private async _openManualCardModal(tickets, saleAmount?: number): Promise<boolean> {
        return new Promise<boolean>(async (resolve) => {
            if (this._waitmodal){
                resolve(false);   // modal en vuelo: no colgar al caller
                return;
            }
            this._waitmodal = true;
            try {
                const modal = await this.modalCtrl.create({
                    component: UiCardPayModal,
                    cssClass: 'modal-card',
                    backdropDismiss: false,
                    componentProps: {
                        tickets: tickets,
                        // Importe explícito (tramo TARJETA de un mixto); sin él el
                        // modal cobraría el total del ticket.
                        saleAmount: saleAmount
                    }
                });

                modal.onDidDismiss().then((detail) => {
                    resolve(!!detail.data);
                });

                await modal.present();
            } catch(_e){
                console.error('[PAY] card modal error', _e);
                resolve(false);
            } finally {
                setTimeout(() => { this._waitmodal = false; }, 100);
            }
        });
    }

    private _print_pressed_timeout = null;
    private _print_ignore_selected = false;

    private get _SelectPrinter(){
        return this._place.MultiplePOS && (!this._place.IsPOSDevice);
    }

    OnPrint(){
        if (!this._print_ignore_selected){
            if (this._SelectPrinter){
                this._PrintTicketModal();
            }
            else {
                this._ticket.DoPrint(false);
            }
        }
    }

    private _waittoprintmodal = false;
    private async _PrintTicketModal(){
        if (!this._waittoprintmodal){            
            this._waittoprintmodal = true;

            const modal = await this.modalCtrl.create({
                component: UiToPrintertModal,
                cssClass: 'ticket-modal',
                componentProps: { 
                    ticket: this._ticket
                }                                    
            });

            await modal.present();
        }

        setTimeout(() => {
            this._waittoprintmodal = false;
        }, 100);
    }

    OnPrintPressed(down){
        if (down){
            if (this._print_pressed_timeout){
                clearTimeout(this._print_pressed_timeout);
                this._print_pressed_timeout = null;
            }

            this._print_pressed_timeout = setTimeout(() => {
                this._PrintTicketModal();

                this._print_ignore_selected = true;
                setTimeout(() => {  // only ignore on current loop
                    this._print_ignore_selected = false;
                }, 0);                
            }, 1000)
        }
        else {
            if (this._print_pressed_timeout){
                clearTimeout(this._print_pressed_timeout);
                this._print_pressed_timeout = null;
            }
        }
    }

    // Prompt paytef/manual. Returns true si aprueba. Va ANTES del
    // commit para que un cancel/deny no toque el ticket original.
    private async _AskPayment(ticket, payment){
        if (payment == 'PAYCASH'){
            return await this.OnPaycash(ticket);
        }

        if (payment == 'PAYCARD'){
            return await this.OnPaycard(ticket);
        }

        return true;
    }

    // Race contra OnCommit emit: Ticket.DoCommit lo dispara antes del
    // super.DoCommit del backend. El editor cierra con el emit, super
    // sigue en background y el toast sale cuando resuelve.
    private async _CommitPaidTicket(ticket, payment){
        this._localPayment = true;
        // El cobro lo hace el camarero SELECCIONADO: sellarlo para que la fila e0 del
        // informe (cobro) salga a su nombre y no al del creador de la comanda.
        ticket.StampWaiter();

        if (ticket.IsEmpty){
            ticket.status = 'CC';
        }
        else {
            ticket.status = 'AC';
        }

        if (ticket.ToInvoice){
            ticket.pendpay = payment;
            return;
        }

        ticket.payment = payment;

        let _commitSub: any = null;
        const _commitEmitted = new Promise<void>((resolve) => {
            _commitSub = ticket.OnCommit.subscribe(() => resolve());
        });

        // Toast solo cuando backend confirma — si sale en el emit
        // temprano y luego falla, el cajero ve confirmacion y despues
        // error, confuso.
        const _commitPromise = ticket.DoCommit();
        _commitPromise.then(_commit => {
            if (_commit){
                this.toast.ShowAlert('primary', this.lang.tr('@place_ticket_sent_toast'), 2000);
            }
            else {
                // El editor ya se cerro con el emit temprano (OnCommit): el cobro NO esta
                // en el servidor. Lote rechazado -> el rescate lo reintenta; fallo fiscal
                // (firma/alta) -> no se envio nada y NO se reintenta solo. En ambos casos
                // el cajero no debe volver a cobrarlo (mismo aviso que ui-view).
                this.toast.ShowAlert('danger', this.lang.tr((ticket.LastCommitFailure == 'fiscal') ? '@ticket_commit_fiscal_failed' : '@ticket_commit_failed'), 12000);
            }
        });

        try {
            await Promise.race([_commitEmitted, _commitPromise]);
        } finally {
            if (_commitSub){
                _commitSub.unsubscribe();
            }
        }
    }

    async OnCommit(payment, authorized = false){
        // Re-check de permiso en el COMMIT (no solo al abrir el editor): si es una
        // modificación y el usuario efectivo (lock-aware) no tiene AllowTicketUpdate
        // —p.ej. bloqueo/cambio de camarero con el editor ya abierto— no se envía.
        // EXCEPCIÓN `authorized`: cuando el pago YA se autorizó y cobró (pago mixto),
        // el commit DEBE completarse aunque el lock saltara durante el cobro; si no, el
        // dinero quedaría movido sin registrar y un reintento recobraría (doble cargo).
        // La autorización se hizo antes de cobrar (OnMixedPayment:2076).
        if (!authorized && this.IsTicketUpdate && !this._place.AllowTicketUpdate(this._user)){
            return;
        }
        // Guard re-entrante: un doble-tap del cajero durante el commit (server
        // lento) dispara N DoCommit del MISMO ticket -> N rectificaciones /
        // doble cobro. _committing (via IsCommitting) deshabilita los botones,
        // pero SOLO se activa durante el commit real (dentro de _OnCommitFlow,
        // tras aprobar el pago) — NO durante el modal de efectivo/datafono: ese
        // va por encima (no deja re-tocar) y si se cancela con la X no debe
        // dejar los botones colgados en gris (#1647).
        if (this._committing){
            return;
        }
        await this._OnCommitFlow(payment);
    }

    private async _OnCommitFlow(payment){
        // abort if the underlying ticket has already been paid elsewhere
        // (another terminal committed payment while this editor was open).
        // immediate=true: es una acción explícita del cajero, no un refresh
        // de fondo — abortamos/cerramos al instante sin la confirmación de
        // dos pasos (abortar es la dirección segura: nunca comiteamos un
        // reembolso contra un ticket ya pagado).
        if (this._CheckPaidElsewhere(true)){
            return;
        }
        // igual para movida/juntada/rechazada/cancelada en otro dispositivo:
        // abortar el commit para no pisar en CopyOf un cambio externo. immediate=
        // true (accion explicita del cajero, sin la confirmacion de 2 pasos).
        if (this._CheckChangedElsewhere(true)){
            return;
        }

        // Defensa: no re-cobrar un prepago QR que YA estaba pagado al abrir.
        // PAYMIXED nunca se salta: el cobro mixto es siempre un cargo fresco
        // (_CommitPaidTicket asigna payment; OnMixedPayment ya no lo hace antes).
        if (payment && payment != 'PAYMIXED' && this._initialIsPaid){
            let _paidSrc = (this._ticket.CopyOf as Ticket) || this._ticket;
            if (_paidSrc.IsPaid){
                console.info("[TICKET EDIT] Skip pay: ticket already paid (" + _paidSrc.orderno + ")");
                return;
            }
        }

        let _ticket = this._ticket;
        let _copyof = _ticket.CopyOf as Ticket;

        // Open paytef against the EDITOR's COPY. The COPY already holds
        // every pending edit, so paytef sees the correct total. No
        // destructive op runs before this point — if the cashier cancels
        // or the terminal denies, the COPY (and _ticketchanges) keep the
        // pending edits intact and the editor stays interactive without
        // any commit or state mutation.
        let _approved = await this._AskPayment(_ticket, payment);
        if (!_approved){
            return;
        }

        // A partir de aqui SI hay commit: bloqueamos los botones (anti doble-tap
        // del commit lento). El finally garantiza el reset aunque algo lance, asi
        // que el boton nunca se queda colgado en gris. NO abarca _AskPayment (el
        // modal) a proposito: cancelarlo con la X no debe bloquear nada (#1647).
        this._committing = true;
        try {
            // Paytef stashes the autoprint terminal on the COPY (the instance
            // the modal received). DoCommit consumes _pendingPaytefTerminal
            // per-instance — COPY and ORIGINAL have distinct _uuid (Copy()
            // generates a fresh one) and the ORIGINAL starts with the slot at
            // null. Capture the stash from the COPY before swapping to the
            // ORIGINAL, then re-stash so DoCommit on the ORIGINAL emits the
            // receipt PD+QR print on datafono.
            let _paytefStash = _ticket.PendingPaytefTerminal;

            if (_copyof){
                _ticket = this._ApplyChanges(_copyof, _ticket.discounts);
            }
            _ticket.comments = this._notes;

            if (_paytefStash && _ticket !== this._ticket){
                _ticket.RequestPaytefAutoPrint(_paytefStash);
            }

            await this._CommitPaidTicket(_ticket, payment);

            this._place.DelCart(_ticket);

            // prepare the next bar ticket (if continious)
            if (this._continious){
                this.CreateTicket(null, true);
            }

            // otherwise: close the ticket view
            else {
                this._OnExit(null);
            }
        } finally {
            this._committing = false;
        }
    }

    OnViewTable(table){
        this._onPanel.emit({
            scnd: 'opts',
            args: {
                qrcode: table
            }
        });
    }

    OnPrepare(){
        this._ticket.status = 'RD';
        if (this._ticket.DoCommit()){
            this._place.DoRefresh('TICKET', true);
        }        
    }

    get CanSeparate(){
        let _toprepare = this._ticket.ProductsToPrepare;

        let _lastitem = (_toprepare.length == 0) ? null : _toprepare[_toprepare.length-1].product;
        if (_lastitem?.status == 'SP'){
            return false;   // can not add separator after separator
        }

        return true;
    }

    OnSeparate(){
        let _ticketproduct = new TicketProduct(null, this.data);
        if (_ticketproduct){
            _ticketproduct.ticket = this._ticket;
            _ticketproduct.status = 'SP';
        }

        this._ticket.CartAdd(_ticketproduct);
    }

    async OnCancelOrder(){
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@ticket_cancel_confirm_tittle'),
            message: this.lang.tr('@ticket_cancel_confirm_message'),
            backdropDismiss: false,
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);

                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        alert.onDidDismiss().then((detail:any) => {
            if (detail.data == true){
                this.OnCancel();
            }
        });

        await alert.present();
    }

    async OnClearOrder(){
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@ticket_remove_all_confirm_tittle'),
            message: this.lang.tr('@ticket_remove_all_confirm_message'),
            backdropDismiss: false,
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);

                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        alert.onDidDismiss().then((detail:any) => {
            if (detail.data == true){
                this._ticket.CartClear();
            }
        });

        await alert.present();
    }

    SelectTicket(_ticket){  
        this._OnExit(_ticket);
    }

    private _CanClose(){
        return new Promise((resolve) => {
            if (!this.IsPristine){  // check if products list is updated
                console.log("[EDIT TICKET] confirm changes lost");
                const alert = this.alertCtrl.alert({
                    header: this.lang.tr('@changes_lost_confirm_tittle'),
                    message: this.lang.tr('@changes_lost_confirm_message'),
                    backdropDismiss: false,
                    buttons: [{
                        text: this.lang.tr('@yes'),
                        handler: () => {
                            setTimeout(() => {
                                alert.dismiss(true);
                            }, 100);
        
                            return false;
                        }
                    }, {
                        text: this.lang.tr('@no'),
                        handler: () => {
                            setTimeout(() => {
                                alert.dismiss(false);
                            }, 100);
                            return false;
                        }
                    }]
                });
        
                alert.onDidDismiss().then((detail:any) => {
                    resolve(detail.data);
                });
        
                alert.present();    
            }
            else {
                resolve(true);
            }        
        });
    }

    OnCartItemDrop(event, _items){
        let _prev = event.previousIndex;
        let _next = event.currentIndex;

        this._ticket.CartSort(_items[_prev], _next - _prev, _items);
        this.change.markForCheck();
    }

    /****************************/
    /* MENU OPTIONS             */
    /****************************/

    private _showOptions = false;
    get ShowOptions(){
        return this._showOptions;
    }

    set ShowOptions(value){

        this._showOptions = value;
        this.change.markForCheck();
    }

    OnTicketOptions(){
        this.ShowOptions = !this.ShowOptions;
    }

    public _action = 'edit';
    public _expand = false;

    private set Action(value){
        this._action = value;
        switch(this._action){
            case 'edit':
                this._expand = false;
                break;
            case 'table':
                this._expand = false;
                break;
            case 'merge':
                this._expand = false;
                break;                
            case 'split':
                this._expand = true;
                break;
        }

        this.change.markForCheck();
    }

    CancelAction(){
        this.Action = 'edit';
    }

    /************************/
    /* CHANGE TABLE         */
    /************************/

    public _qrcode = null;

    private _tbselect = null;
    get TableSelect(){
        if (this._tbselect == null){
            let _tables = [ null ];
            for(let _table of this._place.tables){
                if (_table.IsValid && !_table.IsError){
                    _tables.push(_table);
                }
            }

            _tables = _tables.sort((a, b) => {
                if (!a || !b){
                    return !a ? -1 : 1;
                }
    
                return parseInt(a.number) - parseInt(b.number);
            });

            
            let _options = [];
            for(let _table of _tables){
                if (!_table){
                    _options.push({
                        text: this.lang.tr('@place_ticket_filter_table_bar'),
                        value: null
                    });
                }
                else {
                    _options.push({
                        text: this.lang.tr('@place_ticket_filter_table_number', [ _table.alias || _table.number ]),
                        value: _table
                    });    
                }
            }

            this._tbselect = _options;
        }

        return this._tbselect;
    }

    async OnTicketTable(qrcode){
        if (qrcode != this._ticket.qrcode){
            this._ticket.qrcode = qrcode;
            this._ticket.DoUpdate();    
            this.Action = 'edit';   // completed    
        }
    }

    async TicketTable(){
        this._qrcode = this._ticket.qrcode;

        this.Action = 'table';
        setTimeout(() => {
            this.ShowOptions = false;
        }, 100);
    } 
    
    /************************/
    /* PAYMENT OPTIONS      */
    /************************/

    private _paymentForm = null;

    get PaymentForm(){
        if (this._paymentForm == null){
            let _cash = null;
            let _card = null;

            this._paymentForm = new FormGroup({
                cash: new FormControl(_cash, [
                    Validators.max(this.CashpayMaxPrice),
                    Validators.required
                ]),
                card: new FormControl(_card, [
                    Validators.required
                ])
            })
        }
        return this._paymentForm;
    }

    TicketMixedPayment(){
        if (this.Action != 'mixed'){
            this.Action = 'mixed';
            setTimeout(() => {
                this.ShowOptions = false;
            }, 100);    
        }
    }

    OnPaymentCashChanged(){
        let _cash = this.PaymentForm.get('cash');
        let _card = this.PaymentForm.get('card');

        let _cashvalue = Number(_cash.value);
        if (_cashvalue <= this._ticket.price){
            let _cardvalue = this._ticket.price - _cashvalue; 
            if (_cashvalue){
                _cash.setValue(parseFloat(_cashvalue.toString().replace(",", ".")).toFixed(2));            
                _card.setValue(parseFloat(_cardvalue.toString().replace(",", ".")).toFixed(2))
            }
            else {
                _cash.setValue(null);
                _card.setValue(null);
            }    
        }
        else {
            _cash.setValue(parseFloat(_cashvalue.toString().replace(",", ".")).toFixed(2));            
        }
    }

    OnPaymentCardChanged(){
        let _cash = this.PaymentForm.get('cash');
        let _card = this.PaymentForm.get('card');

        let _cardvalue = Number(_card.value);
        if (_cardvalue <= this._ticket.price){
            let _cashvalue = this._ticket.price - _cardvalue; 
            if (_cardvalue){
                _card.setValue(parseFloat(_cardvalue.toString().replace(",", ".")).toFixed(2));            
                _cash.setValue(parseFloat(_cashvalue.toString().replace(",", ".")).toFixed(2))
            }
            else {
                _card.setValue(null);
                _cash.setValue(null);
            }    
        }
        else {
            _card.setValue(parseFloat(_cardvalue.toString().replace(",", ".")).toFixed(2));            
        }
    }

    get PaymentReady(){
        let _total = 0;

        _total += Number(this.PaymentForm.get('cash').value);
        _total += Number(this.PaymentForm.get('card').value);

        return (_total == this._ticket.price);
    }

    async OnMixedPayment(){
        // Autorizar ANTES de empezar a cobrar: si es una modificación y el usuario
        // efectivo no tiene permiso, no arrancamos el flujo (si no, se cobraba y el
        // guard de OnCommit abortaba DESPUÉS -> dinero movido sin commit y un
        // reintento recobraba).
        if (this.IsTicketUpdate && !this._place.AllowTicketUpdate(this._user)){
            return;
        }
        try {
            // NO setear payment aqui: PAYMIXED → status PD → IsPaid=true y el
            // guard de OnCommit abortaba el cobro. Ademas, si falla datáfono/
            // CashGuard el ticket quedaba marcado pagado sin commit.
            // _CommitPaidTicket asigna payment=PAYMIXED al confirmar.

            let _ticketcash = null;
            let _ticketcard = null;

            for (let _mixedpayment of this._ticket.mixed){
                if (_mixedpayment.payment == 'PAYCASH'){
                    _ticketcash = _mixedpayment;
                }

                if (_mixedpayment.payment == 'PAYCARD'){
                    _ticketcard = _mixedpayment;
                }
            }

            _ticketcash = _ticketcash || new TicketMixedPayment(null, this.data);
            let _cashAmount = Number(this.PaymentForm.get('cash').value);
            if (_ticketcash){
                _ticketcash.payment = 'PAYCASH',
                _ticketcash.amount = _cashAmount;
                _ticketcash.ticket = this._ticket;
            }

            _ticketcard = _ticketcard || new TicketMixedPayment(null, this.data);
            let _cardAmount = Number(this.PaymentForm.get('card').value);
            if (_ticketcard){
                _ticketcard.payment = 'PAYCARD',
                _ticketcard.amount = _cardAmount;
                _ticketcard.ticket = this._ticket;
            }

            // Orden money-safe (igual que OnTicketReturn): EFECTIVO primero. Si
            // el tramo efectivo se cancela/falla, abortamos ANTES de cobrar la
            // tarjeta, asi nunca queda un cargo en tarjeta ya aprobado sin venta
            // (que habria que abonar a mano; peor en un ticket nuevo sin orderno
            // para referenciar el abono). La tarjeta, cara de deshacer, va la
            // ULTIMA: si falla, no hay nada previo que revertir electronicamente.
            let _cashPaymentSuccess = true;
            if (_cashAmount > 0){
                _cashPaymentSuccess = await this._ProcessMixedCashPayment(_cashAmount);
            }
            if (!_cashPaymentSuccess){
                return; // Cash payment failed or was cancelled
            }

            let _cardPaymentSuccess = true;
            if (_cardAmount > 0){
                _cardPaymentSuccess = await this._ProcessMixedCardPayment(_cardAmount);
            }
            if (!_cardPaymentSuccess){
                // Tarjeta falló/canceló tras cobrar el efectivo por el cajón:
                // devolver el efectivo ya depositado o el reintento lo duplica
                // (mismo criterio que ui-view._returnMixedCash). Solo si entró
                // REALMENTE en el cajón: en efectivo manual/fallback (o sin
                // cajón) un CASH_OUT dispensaría saldo ajeno.
                if (_cashAmount > 0 && _cashPaymentSuccess && this._mixedCashInDrawer){
                    await this._returnMixedCash(_cashAmount);
                }
                return;
            }

            // Clear the form only after successful payment
            setTimeout(() => {
                this._paymentForm = null;   // clear the form
            }, 0);

            if (_ticketcash && _ticketcard){
                this._ticket.AddMixed(_ticketcash);
                this._ticket.AddMixed(_ticketcard);
            }
            
            this.Action = 'edit';       // close any pending action before creating new ticket (if continuous)
            // authorized=true: el pago ya se cobró (efectivo+tarjeta arriba); el commit
            // debe completarse aunque el lock saltara durante el cobro (evita el dinero
            // movido sin registrar y el doble cargo al reintentar).
            this.OnCommit('PAYMIXED', true);
        } catch(e){
            throw e;
        }
    }

    private async _ProcessMixedCardPayment(cardAmount: number): Promise<boolean>{
        let _cgInfo = this._place.smartdrawerForTicket(this._ticket);

        // Selector de datáfono UNIFICADO (igual que OnPaycard): con datáfonos de
        // varios tipos (PROSEGUR cashless + PAYTEF) o 2+ unidades, deja ELEGIR al
        // cajero en vez de ir directo al primero. Faltaba en el tramo TARJETA del
        // pago mixto -> iba directo al datáfono 1. Con un solo tipo devuelve
        // 'default' y sigue el flujo de siempre.
        if (this._waitmodal){ return false; }
        this._waitmodal = true;
        let _choice;
        try {
            _choice = await chooseCardTerminal(this.modalCtrl, this.lang, this._place, this.view, _cgInfo);
        } catch(e){
            // En el MIXTO el efectivo YA se cobró. Si el selector lanza (modal no se
            // pudo crear/presentar/cerrar), NO propagamos: OnMixedPayment solo hace
            // rethrow y se saltaría la devolución del efectivo. Devolvemos false para
            // que el tramo tarjeta "falle" limpio y se dispare la compensación.
            console.warn('[PAY MIXTO] selector de datáfono falló; se trata como fallo de tarjeta', e);
            return false;
        } finally {
            this._waitmodal = false;
        }
        if (_choice.kind !== 'default'){
            if (_choice.kind === 'cancelled'){ return false; }
            if (_choice.kind === 'manual'){
                if (!this._place.CardModal){ return true; }
                return this._openManualCardModal([ this._ticket ], cardAmount);
            }
            if (_choice.kind === 'paytef'){
                return this._openPaytefModal([ this._ticket ], _choice.terminal, cardAmount);
            }
            // prosegur: cobro cashless por el datáfono YA elegido (sin re-preguntar).
            if (this._waitmodal){ return false; }
            let _deps = this._cashlessDeps(cardAmount);
            _deps.chosenTerminal = _choice.terminal;
            this._waitmodal = true;
            let _r: boolean | null;
            try {
                _r = await tryCashlessCardPayment(this.devsvc, _deps, [ this._ticket ], _cgInfo);
            } finally {
                this._waitmodal = false;
            }
            return _r === null ? false : _r;
        }

        // default (un solo tipo de datáfono): flujo de siempre. El cajón con
        // datafono (PROSEGUR/CASHLOGY) cobra la tarjeta por él (CHARGE_CASHLESS)
        // con su importe explícito; si no puede, devuelve null y cae a paytef.
        if (this._waitmodal){
            return false;
        }
        this._waitmodal = true;
        let _cgCard: boolean | null;
        try {
            _cgCard = await tryCashlessCardPayment(this.devsvc, this._cashlessDeps(cardAmount), [ this._ticket ], _cgInfo);
        } finally {
            this._waitmodal = false;
        }
        if (_cgCard !== null){
            return _cgCard;
        }

        let _terminals = resolvePaymentTerminals(this._place, this.view);

        if (_terminals.length > 0){
            let _pick = await pickPaymentTerminal(this.modalCtrl, this.lang, _terminals, this.view);
            if (!_pick){
                return false;
            }
            if (isPaymentTerminalManual(_pick)){
                if (!this._place.CardModal){
                    return true;
                }
                return this._openManualCardModal([ this._ticket ], cardAmount);
            }
            return this._openPaytefModal([ this._ticket ], _pick, cardAmount);
        }

        if (this._place.CardModal){
            return this._openManualCardModal([ this._ticket ], cardAmount);
        }

        return true;
    }

    /**
     * Devuelve al cliente el efectivo ya cobrado por el cajón cuando la parte
     * TARJETA del pago mixto falla o se cancela (mismo criterio que
     * ui-view._returnMixedCash). Sin esto el efectivo queda dentro sin atribuir
     * (descuadre) y un reintento lo volvería a cobrar. Dispensa por CASH_OUT; si
     * no hay cajón o la devolución falla, avisa al cajero para hacerlo a mano.
     */
    private async _returnMixedCash(cashAmount: number): Promise<void> {
        let _cgInfo = this._place.smartdrawerForTicket(this._ticket);
        let _smartdrawer = _cgInfo?.drawer || null;
        let _cgTill = _cgInfo?.till || null;
        let _tolocal = _cgInfo?.tolocal ?? false;

        if (!_smartdrawer || !_cgTill){
            this.toast.ShowAlert('danger', this.lang.tr('@mixed_cash_return_manual'), 5000);
            return;
        }

        let _target = _smartdrawer.model || 'CASHGUARD';
        let _connection = null;
        let _ok = false;
        try {
            let _host = _cgTill.wshost;
            let _port = _cgTill.wsport ? parseInt(_cgTill.wsport) : 5005;
            _connection = await this.devsvc.Connect(_host, _port, _cgTill.name, _tolocal);
            if (_connection){
                let _resp = await _connection.Send(_target, {
                    command: 'CASH_OUT',
                    transaction_id: Math.floor(Math.random() * 1000000),
                    amount_due: Math.round(cashAmount * 100)
                });
                _ok = !!(_resp && _resp.exec == 'SUCCESS');
            }
        }
        catch(e){
            console.error('[PAY MIXTO] devolución de efectivo tras fallo de tarjeta:', e);
        }
        finally {
            if (_connection){
                this.devsvc.Disconnect(_connection);
            }
        }

        this.toast.ShowAlert(
            _ok ? 'warning' : 'danger',
            this.lang.tr(_ok ? '@mixed_cash_returned' : '@mixed_cash_return_manual'),
            _ok ? 3500 : 5000
        );
    }

    private async _ProcessMixedCashPayment(cashAmount: number): Promise<boolean>{
        // Por defecto el efectivo NO esta en el cajon; solo la rama de deposito
        // real en el cajon lo pone a true (ver mas abajo).
        this._mixedCashInDrawer = false;
        return new Promise<boolean>(async (resolve) => {
            let _cgInfo = this._place.smartdrawerForTicket(this._ticket);
            let _smartdrawer = _cgInfo?.drawer || null;
            let _cgTill = _cgInfo?.till || null;
            let _tolocal = _cgInfo?.tolocal ?? false;

            if (!_smartdrawer || !_cgTill){
                // No CashGuard, cash payment is considered successful (manual handling)
                resolve(true);      // efectivo manual: _mixedCashInDrawer queda false
                return;
            }

            let modal;
            try {
                modal = await this.modalCtrl.create({
                    component: UiCashGuardCashPayModal,
                    cssClass: 'modal-cashguard',
                    backdropDismiss: false,
                    componentProps: {
                        tickets: [ this._ticket ],
                        device: _smartdrawer,
                        amountDue: cashAmount,
                        place: _cgTill,
                        tolocal: _tolocal
                    }
                });
            } catch(_e){
                console.error('[PAY] mixed cash modal error', _e);
                resolve(false);
                return;
            }

            modal.onDidDismiss().then(async (detail) => {
                if (detail.data && detail.data['fallback']){
                    let cashModal;
                    try {
                        cashModal = await this.modalCtrl.create({
                        component: UiCashPayModal,
                        cssClass: 'modal-cash',
                        backdropDismiss: false,
                        componentProps: { 
                            tickets: [ this._ticket ],
                            place: this._place
                        }                                    
                    });
                    } catch(_e){
                        console.error('[PAY] mixed fallback cash modal error', _e);
                        resolve(false);
                        return;
                    }
                    
                    cashModal.onDidDismiss().then((cashDetail) => {
                        if (cashDetail.data){
                            this._ticket.given = cashDetail.data['amount'];
                        }
                        resolve(cashDetail.data != null);
                    });
                    
                    try {
                        await cashModal.present();
                    } catch(e){
                        resolve(false);
                    }
                } else {
                    if (detail.data && detail.data['amount']){
                        this._ticket.given = detail.data['amount'];
                    }
                    let _deposited = detail.data != null && detail.data['amount'] != null;
                    // El efectivo entro en el cajon: si luego falla la tarjeta,
                    // _returnMixedCash puede devolverlo por CASH_OUT.
                    this._mixedCashInDrawer = _deposited;
                    resolve(_deposited);
                }
            });

            try {
                await modal.present();
            } catch(e){
                resolve(false);
            }
        });
    }

    /************************/
    /* PAY TO ACCOUNT       */
    /************************/

    public _account = null;

    private _acselect = null;
    get AccountSelect(){
        if (this._acselect == null){
            let _accounts = [];
            for(let _account of this._place.payaccounts){
                if (_account.IsValid && !_account.IsBlocked){
                    _accounts.push(_account);
                }
            }

            _accounts = _accounts.sort((a, b) => {
                if (a.name == b.name){
                    return 0;
                }

                return (a.name < b.name) ? -1 : 1;
            });

            let _options = [];
            for(let _account of _accounts){
                _options.push({
                    text: _account.name || this.lang.tr('@place_account_no_name'),
                    value: _account
                });    
            }

            this._acselect = _options;
        }

        return this._acselect;
    }

    OnTicketAccount(_account){
        this._account = _account;
    }

    OnPayAccount(_account){
        this._ticket.account = _account;
        this.Action = 'edit'; // close any pending action before creating new ticket (if continuous)
        this.OnCommit('PAYACCOUNT');
    }

    TicketToAccount(){
        this._account = null;

        this.Action = 'account';
        setTimeout(() => {
            this.ShowOptions = false;
        }, 100);
    }

    /************************/
    /* MERGE TICKET         */
    /************************/

    get MergeTickets(){
        let _target = this._ticket.CopyOf as Ticket || this._ticket;

        let _tickets = [];
        for(let _ticket of this._place.tickets){
            if ((_ticket != _target) && (_ticket.qrcode == _target.qrcode) && (_ticket.IsValid) && (_ticket.IsRecent) && (['AC', 'RD'].indexOf(_ticket.status) != -1) && !(_ticket.IsPaid || _ticket.IsToPay)){
                _tickets.push(_ticket);
            }
        }
        return _tickets;
    }

    TicketMerge(){
        this.Action = 'merge';
        setTimeout(() => {
            this.ShowOptions = false;
        }, 100);
    }

    public _mergeinfo = null;
    async OnMergeCancel(){
        if (!this._mergeinfo){
            return;
        }

        let _mergeinfo = this._mergeinfo;
        this._mergeinfo = null;

        if (_mergeinfo){
            // remove the merged products
            for(let _ticketproduct of _mergeinfo.tktprd){
                this._ticket.DelProduct(_ticketproduct);
                _ticketproduct.ticket = _mergeinfo.target;
            }

            // remove the merged discounts
            for (let _ticektdiscount of _mergeinfo.tktdct.toDelete){
                this._ticket.AddDiscount(_ticektdiscount);
            }

            for (let _ticektdiscount of _mergeinfo.tktdct.toInsert){
                this._ticket.DelDiscount(_ticektdiscount);
                _ticektdiscount.ticket = _mergeinfo.target;
            }

            // recalculate ticket changes
            this._ticket.isopen = true;
            this._ticket.DoUpdate();
            this._ticket.isopen = false;    
        }

        this.CancelAction();
    }

    async OnMergeCommit(){
        let _mergeinfo = this._mergeinfo;
        this._mergeinfo = null;

        if (_mergeinfo){
            let _merge = _mergeinfo.target;

            // apply existing changes into the ticket
            let _ticket = this._ticket;
            let _copyof = _ticket.CopyOf as Ticket;
            if (_copyof){
                _ticket = this._ApplyChanges(_copyof, _ticket.discounts);
            }

            _ticket.comments = this._notes;

            // add changes information to ticket
            for(let _ticketproduct of _mergeinfo.tktprd){
                _merge.DelProduct(_ticketproduct);
                _ticketproduct.ticket = _ticket;
                _ticket.AddProduct(_ticketproduct);
            }

            // apply ticket discounts
            for (let _ticketdiscount of _mergeinfo.tktdct.toDelete){
                this._ticket.DelDiscount(_ticketdiscount);
            }

            for (let _ticketdiscount of _mergeinfo.tktdct.toInsert){
                _merge.DelDiscount(_ticketdiscount);
                _ticketdiscount.ticket = this._ticket;
                this._ticket.AddDiscount(_ticketdiscount);
            }

            for (let _ticketdiscount of _mergeinfo.target.discounts){
                _merge.DelDiscount(_ticketdiscount);
            }

            // cancel the merged ticket (it should be empty)
            _merge.DoCancel();

            // recalculate and commit ticket changes
            let _transaction = await this.data.BeginTransaction();
            if (_transaction){
                for(let ticket of [_ticket, _merge]){
                    ticket.isopen = true;
                    ticket.DoUpdate();
                    ticket.isopen = false;

                    _transaction.SendCommit(ticket);
                }

                await _transaction.Flush(true);
                this._place.DoRefresh('TICKET', true);
            }
        }

        this.Action = 'edit';   // completed
    }

    OnTicketMerge(_merge){
        let _products = [];

        _products = _products.concat(_merge.ProductsToRefund);
        _products = _products.concat(_merge.ProductsToPrepare);
        _products = _products.concat(_merge.ProductsDelivered);

        let _ticketproducts = [];   // select all the ticket products
        for(let _product of _products){
            for(let requested of _product.product.requested){
                _ticketproducts.push(requested);
            }
        }
        
        let _ticketdiscounts = {
            toInsert: [],       // discounts to be added to the ticket (for price calculation)
            toDelete: []        // discounts to be removed from the ticket
        }

        let _discountwarning = this._MergeWarning(this._ticket, _merge);
        if (_discountwarning){
            for (let _ticketdiscount of this._ticket.discounts){
                _ticketdiscounts.toDelete.push(_ticketdiscount);;
            }
        }
        else {
            for(let _ticketdiscount of _merge.discounts){
                let _applied = this._ticket.discounts.some(
                _discount => {
                    return (_discount.discount == _ticketdiscount.discount);
                });

                if (!_applied){
                    _ticketdiscounts.toInsert.push(_ticketdiscount);
                }
            }
        }

        // complete the merge information
        this._mergeinfo = {
            tktprd: _ticketproducts,
            tktdct: _ticketdiscounts,
            dscwrn: _discountwarning,
            target: _merge,
            status: _merge.status
        };

        // add the selected products to current (selected will be cancelled)
        for(let _ticketproduct of _ticketproducts){
            _ticketproduct.ticket = this._ticket;
            this._ticket.CartAdd(_ticketproduct);
        }

        // add the selected discounts to current (selected will be cancelled)
        for (let _ticketdiscount of _ticketdiscounts.toDelete){
            _ticketdiscount.status = 'DE';
        }

        for (let _ticketdiscount of _ticketdiscounts.toInsert){
            _ticketdiscount.ticket = this._ticket;
            this._ticket.AddDiscount(_ticketdiscount);
        }

        // recalculate ticket changes
        this._ticket.isopen = true;
        this._ticket.DoUpdate();
        this._ticket.isopen = false;
    }

    private _MergeDiscounts(_ticket1, _ticket2){
        let _d1 = _ticket1?.discounts || [];
        let _d2 = _ticket2?.discounts || [];

        let _discounts = new Set <Discount> ();
        for (let _discount of _d1.concat(_d2)){
            if (_discount.IsValid){
                _discounts.add(_discount.discount);
            }
        }
        return Array.from(_discounts);
    }

    private _MergeWarning(_ticket1, _ticket2){
        if (!_ticket1 || !_ticket2){
            return false;
        }

        let _discounts = this._MergeDiscounts(_ticket1, _ticket2);
        if (_discounts.length < 2){
            return false;
        }

        let _incompatible = _discounts.some(_discount => {
            return (_discount.cumulative == false);    // more than one discount and at least one is not cumulative
        })

        return _incompatible;
    }

    get MergeWarning(){
        if (this._mergeinfo){
            return this._mergeinfo.dscwrn;
        }
        
        return this._MergeWarning(this._ticket, this._mergeinfo?.target || null);
    }

    /************************/
    /* SPLIT TICKET         */
    /************************/

    TicketSplit(){
        this.Action = 'split';
        setTimeout(() => {
            this.ShowOptions = false;
        }, 100);
    }

    private get HasSelectedProducts(){
        let _products = [];

        _products = _products.concat(this._ticket.ProductsToPrepare);
        _products = _products.concat(this._ticket.ProductsDelivered);

        for(let _product of _products){
            let _some = _product.product.requested.some(
            requested => {
                return (requested['isSelected'] == true);
            });

            if (_some){
                return true;
            }
        }

        return false;
    }

    private get AllSelectedProducts(){
        let _products = [];

        _products = _products.concat(this._ticket.ProductsToPrepare);
        _products = _products.concat(this._ticket.ProductsDelivered);

        for(let _product of _products){
            let _every = _product.product.requested.every(
            requested => {
                return (requested['isSelected'] == true);
            });

            if (!_every){
                return false;
            }
        }

        return true;
    }

    async OnTicketSplit(){
        // Guard contra doble-click: el await Flush(true) tarda lo
        // suficiente para que el cajero pueda volver a pulsar antes
        // de que el editor cierre. Sin esto el segundo click ejecuta
        // OnTicketSplit otra vez, recoge los mismos isSelected
        // (aun no se han limpiado, el editor sigue montado), crea
        // un segundo _split, e intenta mover de nuevo los productos
        // — pero como ya estan en el primer _split, el DelProduct
        // del original falla silenciosamente y AddProduct los pega
        // al segundo split, dejando al primero (#152 en el bug
        // reportado) huerfano y vacio mientras el segundo (#153)
        // se queda con todos los productos.
        if (this._splitting){
            return;
        }
        this._splitting = true;

        try {
            let _products = [];

            _products = _products.concat(this._ticket.ProductsToPrepare);
            _products = _products.concat(this._ticket.ProductsDelivered);

            let _ticketproducts = [];   // select the products to move
            for(let _product of _products){
                for(let requested of _product.product.requested){
                    if (requested['isSelected']){
                        _ticketproducts.push(requested.CopyOf || requested);
                    }
                }
            }

            // create a new ticket with the products
            let _split = new Ticket(null, this.data);
            if (_split) {
                _split.status = this._ticket.status;
                _split.session = this.data.session;
                _split.waiter = this.data.CurrentWaiter;
                _split.place = this._ticket.place;
                _split.qrcode = this._ticket.qrcode;

                this._place.AddTicket(_split);
            }

            this._place.AddCart(_split);

            // apply existing changes into the ticket
            let _ticket = this._ticket;
            let _copyof = _ticket.CopyOf as Ticket;
            if (_copyof){
                _ticket = this._ApplyChanges(_copyof, _ticket.discounts, false);
            }

            _ticket.comments = this._notes;

            // El ticket ORIGEN (ya resuelto a su CopyOf si se edita sobre copia)
            // también lo edita este usuario al sacarle productos -> se le atribuye
            // igual que a la mitad nueva (último que edita).
            _ticket.waiter = this.data.CurrentWaiter;

            // remove the products from current ticket
            for(let _ticketproduct of _ticketproducts){
                _ticket.DelProduct(_ticketproduct);
                _ticketproduct.ticket = _split;
                _split.AddProduct(_ticketproduct);
            }

            // recalculate and commit ticket changes
            let _transaction = await this.data.BeginTransaction();
            if (_transaction){
                for(let ticket of [ _ticket, _split]){
                    ticket.isopen = true;
                    ticket.DoUpdate(true);
                    ticket.isopen = false;

                    _transaction.SendCommit(ticket);
                }

                await _transaction.Flush(true);
                this._place.DoRefresh('TICKET', true);

                this._OnExit(_split);
            }
        }
        finally {
            this._splitting = false;
        }
    }

    /************************/
    /* CREATE TICKET        */
    /************************/

    async CreateTicket(_table, _ignorechanges = false){
        // remove the previous ticket from the cart
        if (this._ticket){
            this._place.DelCart(this._ticket);
        }

        // create the new ticket (continious mode)
        if (_ignorechanges || await this._CanClose()){
            let _ticket = this._place.CreateTicket(_table);
            this._onPanel.emit({
                main: 'cart',
                scnd: null,
                args: {
                    ticket: _ticket
                }
            });            
        }

        setTimeout(() => {
            this.ShowOptions = false;
        }, 100);        
    }    

    /************************/
    /* CANCEL TICKET        */
    /************************/

    private async _TicketCancel(){
        let _ticket = this._ticket.CopyOf as Ticket || this._ticket;
        if (_ticket){
            this._localCancel = true;   // cancelacion local: no disparar changed-elsewhere
            _ticket.DoCancel();

            await _ticket.DoCommit()
            this.toast.ShowAlert('danger', this.lang.tr('@place_ticket_cancelled_toast'), 2000);
        }

        this._OnExit(null);
    }

    async TicketCancel(){
        setTimeout(() => {
            this.ShowOptions = false;
        }, 100);

        let alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_cancel_ticket_confirm_tittle'),
            message: this.lang.tr('@place_cancel_ticket_confirm_message'),
            backdropDismiss: false,
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        alert.onDidDismiss().then((detail:any) => {
            if (detail && detail.data){
                this._TicketCancel();
            }
        });

        await alert.present();
    }
}
